/**
 * 选择用户弹窗（公用）
 * 左侧：空间成员列表（搜索 + 分页 + 勾选）
 * 右侧：已选择列表（可移除）
 * 数据来源：trpc.spaceMembers.list（空间成员，后端从 context 取 spaceId）
 */
import * as React from 'react';
import type { Selection } from 'react-aria-components';
import {
  Avatar,
  Button,
  Description,
  Label,
  ListBox,
  Modal,
  Pagination,
  SearchField,
  Surface,
} from '@heroui/react';
import { IconX as X } from '@tabler/icons-react';
import { useAppSelector } from '@/stores/hooks';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

export type UserSelectItem = {
  userId: string;
  nickname?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
};

export interface UserSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (selected: UserSelectItem[]) => void;
  title?: string;
  /** 已选中的 userId 列表（用于回显，如当前管理员） */
  excludeUserIds?: string[];
}

const PAGE_SIZE = 10;

function selectionToIdSet(keys: Selection, visibleIds: string[]): Set<string> {
  if (keys === 'all') return new Set(visibleIds);
  return new Set(Array.from(keys, (k) => String(k)));
}

export function UserSelectDialog({
  open,
  onOpenChange,
  onConfirm,
  title = '选择用户',
  excludeUserIds = [],
}: UserSelectDialogProps) {
  const user = useAppSelector((state) => state.auth.user);
  const spaceId = user?.spaces?.[0]?.id;
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [keyword, setKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selected, setSelected] = React.useState<UserSelectItem[]>([]);

  const { data, isLoading } = trpc.spaceMembers.list.useQuery(
    {
      page,
      pageSize: PAGE_SIZE,
      keyword: keyword.trim() || undefined,
      sortBy: 'joinedAt',
      sortOrder: 'asc',
    },
    { enabled: open && isHydrated && isAuthenticated && !!spaceId }
  );

  const items = (data?.items ?? []) as Array<{
    id: string;
    userId?: string | null;
    user?: {
      id: string;
      username?: string | null;
      nickname?: string | null;
      avatarUrl?: string | null;
    } | null;
  }>;

  const totalPages = data?.totalPages ?? 0;
  const totalItems = data?.total ?? 0;

  const visibleItems = React.useMemo(
    () =>
      items.filter((item) => {
        const uid = item.userId ?? item.user?.id;
        return uid && !excludeUserIds.includes(uid);
      }),
    [items, excludeUserIds]
  );

  const selectedKeys = React.useMemo(
    () => new Set(selected.map((s) => s.userId)),
    [selected]
  );

  const handleUserSelectionChange = React.useCallback((keys: Selection) => {
    const visibleIds = visibleItems
      .map((i) => i.userId ?? i.user?.id)
      .filter((id): id is string => Boolean(id));
    const visibleIdSet = new Set(visibleIds);
    const nextKeys = selectionToIdSet(keys, visibleIds);

    setSelected((prev) => {
      const kept = prev.filter((p) => !visibleIdSet.has(p.userId));
      const newlySelected: UserSelectItem[] = [];
      for (const id of nextKeys) {
        if (!visibleIdSet.has(id)) continue;
        const item = visibleItems.find((i) => (i.userId ?? i.user?.id) === id);
        if (!item) continue;
        newlySelected.push({
          userId: id,
          nickname: item.user?.nickname ?? null,
          username: item.user?.username ?? null,
          avatarUrl: item.user?.avatarUrl ?? null,
        });
      }
      return [...kept, ...newlySelected];
    });
  }, [visibleItems]);

  const removeSelected = React.useCallback((userId: string) => {
    setSelected((prev) => prev.filter((p) => p.userId !== userId));
  }, []);

  const resetLocalState = React.useCallback(() => {
    setSelected([]);
    setKeyword('');
    setPage(1);
  }, []);

  const handleConfirm = React.useCallback(() => {
    onConfirm(selected);
    onOpenChange(false);
    resetLocalState();
  }, [onConfirm, onOpenChange, selected, resetLocalState]);

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) resetLocalState();
      onOpenChange(next);
    },
    [onOpenChange, resetLocalState]
  );

  const displayName = (item: (typeof items)[number]) =>
    item.user?.nickname || item.user?.username || `用户 ${item.userId ?? item.user?.id ?? ''}`;

  const displayDescription = (item: (typeof items)[number]) => {
    const u = item.user?.username?.trim();
    if (u) return `@${u}`;
    return '—';
  };

  const startItem = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, totalItems);

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog className="flex h-[500px] max-h-[85vh] max-w-2xl flex-col">
            <Modal.Header>
              <Modal.Heading className="text-xl font-medium">{title}</Modal.Heading>
            </Modal.Header>

            <Modal.Body className="flex min-h-0 flex-1 flex-row gap-0">
              <div className="flex h-full min-h-0 w-3/5 min-w-0 shrink-0 flex-col overflow-hidden border-r border-[var(--border)]">
                <div className="flex h-12 shrink-0 items-center border-b border-[var(--border)] px-4">
                  <h4 className="text-sm font-medium">空间成员</h4>
                </div>
                <div className="flex h-14 shrink-0 items-center px-4">
                  <SearchField
                    aria-label="搜索成员"
                    value={keyword}
                    onChange={(v) => {
                      setKeyword(v);
                      setPage(1);
                    }}
                  >
                    <SearchField.Group>
                      <SearchField.SearchIcon />
                      <SearchField.Input placeholder="输入用户名或昵称搜索" />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>
                </div>
                <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-4 py-2">
                  <Surface className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl shadow-surface">
                    <div className="min-h-[120px] flex-1 overflow-y-auto p-1">
                      {isLoading ? (
                        <p className="text-muted px-2 py-2 text-sm">加载中…</p>
                      ) : visibleItems.length === 0 ? (
                        <p className="text-muted px-2 py-2 text-sm">暂无成员</p>
                      ) : (
                        <ListBox
                          aria-label="空间成员"
                          selectionMode="multiple"
                          selectedKeys={selectedKeys}
                          onSelectionChange={handleUserSelectionChange}
                        >
                          {visibleItems.map((item) => {
                            const uid = item.userId ?? item.user?.id ?? '';
                            return (
                              <ListBox.Item
                                key={item.id}
                                id={uid}
                                textValue={`${displayName(item)} ${displayDescription(item)}`}
                              >
                                <Avatar size="sm">
                                  {item.user?.avatarUrl ? (
                                    <Avatar.Image
                                      src={item.user.avatarUrl}
                                      alt={displayName(item)}
                                    />
                                  ) : null}
                                  <Avatar.Fallback>
                                    {(item.user?.nickname || item.user?.username || '?')
                                      .charAt(0)
                                      .toUpperCase()}
                                  </Avatar.Fallback>
                                </Avatar>
                                <div className="flex min-w-0 flex-1 flex-col">
                                  <Label className="truncate">{displayName(item)}</Label>
                                  <Description className="text-muted truncate text-xs">
                                    {displayDescription(item)}
                                  </Description>
                                </div>
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            );
                          })}
                        </ListBox>
                      )}
                    </div>
                  </Surface>

                  <Pagination className="w-full shrink-0">
                    <Pagination.Summary>
                      {startItem} 至 {endItem} 共 {totalItems} 条
                    </Pagination.Summary>
                    <Pagination.Content>
                      <Pagination.Item>
                        <Pagination.Previous
                          isDisabled={page <= 1 || totalPages <= 0}
                          onPress={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          <Pagination.PreviousIcon />
                          <span>上一页</span>
                        </Pagination.Previous>
                      </Pagination.Item>
                      <Pagination.Item>
                        <Pagination.Next
                          isDisabled={page >= totalPages || totalPages <= 0}
                          onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                          <span>下一页</span>
                          <Pagination.NextIcon />
                        </Pagination.Next>
                      </Pagination.Item>
                    </Pagination.Content>
                  </Pagination>
                </div>
              </div>

              <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <div className="flex h-12 shrink-0 items-center border-b border-[var(--border)] px-4">
                  <h4 className="text-sm font-medium">已选择</h4>
                </div>
                <div className="flex h-14 shrink-0 items-center px-4">
                  <p className="text-muted text-sm">{selected.length}个用户</p>
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-2">
                  <Surface className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl shadow-surface">
                    <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
                      {selected.map((u) => (
                        <div
                          key={u.userId}
                          className="flex items-center gap-2 rounded-lg px-2 py-1.5"
                          style={{
                            background:
                              'color-mix(in oklch, var(--surface-secondary) 70%, transparent)',
                          }}
                        >
                          <Avatar className="size-6" size="sm">
                            {u.avatarUrl ? (
                              <Avatar.Image
                                src={u.avatarUrl}
                                alt={u.nickname || u.username || ''}
                              />
                            ) : null}
                            <Avatar.Fallback className="text-xs">
                              {(u.nickname || u.username || '?').charAt(0).toUpperCase()}
                            </Avatar.Fallback>
                          </Avatar>
                          <div className="flex min-w-0 flex-1 flex-col">
                            <Label className="truncate text-sm">
                              {u.nickname || u.username || u.userId}
                            </Label>
                            <Description className="text-muted truncate text-xs">
                              {u.username?.trim() ? `@${u.username}` : '—'}
                            </Description>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSelected(u.userId)}
                            className="rounded p-0.5 hover:bg-[var(--surface-secondary)]"
                            aria-label="移除"
                          >
                            <X className="size-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </Surface>
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="ghost" onPress={() => handleModalOpenChange(false)}>
                取消
              </Button>
              <Button
                isDisabled={selected.length === 0}
                onPress={handleConfirm}
              >
                确定
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
